// @flow

import * as React from 'react';

import { toUnicode } from 'punycode.js';
import { connect } from 'react-redux';
import { compose } from 'redux';

import { withPageMeta } from '@gandi/app-runner';

import { needsAuth } from '@gandi/authentication';
import { updateProduct, selectOneProductById, type Product } from '@gandi/cart';
import { notifyDanger } from '@gandi/notifications';
import { withTranslator, type TranslatorProps } from '@gandi/translate';
import { Alert, Button, Base } from '@gandi/ui';

import { withLogger } from 'gandi.redux-logger';
import type { GandiLogger } from 'gandi.redux-logger';

import { Intro } from 'src/Layout';
import { Layout } from 'src/components/Layout';
import { withRouter } from 'src/helpers/withRouter';
import type { Dispatch, WithRouterProps, FullState } from 'src/types';

import AuthInfoForm from './components';

import type { AuthInfoFormValues } from './types';

export type Params = {
  cartId: string,
  productId: string,
};

type CartAuthInfoConfigureProps = {
  dispatch: Dispatch,
  translator: TranslatorProps,
  logger: GandiLogger,
  product: ?Product,
  ...WithRouterProps<Params>,
};

class AuthInfo extends React.Component<CartAuthInfoConfigureProps> {
  /**
   * Save the authinfo code in product payload
   * @param  {AuthInfoFormValues} data The form data
   * @return {Promise}
   */
  saveAuthInfo: (data: AuthInfoFormValues) => Promise<void> = (
    data: AuthInfoFormValues,
  ) => {
    const {
      translator: { __ },
      logger,
      params: { cartId },
      product,
    } = this.props;

    if (!product) {
      return Promise.resolve();
    }

    const payload = {
      ...product.payload,
      authinfo: data.authinfo,
    };

    return this.props
      .dispatch(
        updateProduct({ productId: this.props.params.productId, payload }),
      )
      .then(() => this.props.navigate(`/cart/${cartId}/overview`))
      .catch(err => {
        logger.error(
          { err },
          'Error during the PATCH of the cart payload with authinfo code.',
        );
        this.props.dispatch(
          notifyDanger(__('An error occurred when saving your authinfo code')),
        );
      });
  };

  renderContent(): React.Node {
    const {
      translator: { __ },
      product,
      params: { cartId },
    } = this.props;

    if (!product) {
      return (
        <Base marginBottom="base">
          <Alert
            intent="danger"
            message={__('The product has not been found!')}
          />
        </Base>
      );
    }

    // $FlowFixMe[incompatible-call]
    const cleanIdentifier = toUnicode(product.fqdn);

    const formButtons = [
      <Button
        key="cancel"
        kind="outline"
        grouped
        to={`/cart/${cartId}/overview`}
      >
        {__('Cancel')}
      </Button>,
      // $FlowFixMe FORM_ID is static
      <Button key="submit" grouped form={AuthInfoForm.FORM_ID} type="submit">
        {__('Submit')}
      </Button>,
    ];

    return (
      <>
        <Intro
          title={__('Authinfo code for %(fqdn)s', { fqdn: cleanIdentifier })}
        />

        <Base marginBottom="base">
          <Alert
            intent="info"
            title={__('Authorization code for the domain you are transfering')}
            message={__(
              '<p>The current domain may need an authorization code. You need to contact your ' +
                'current registrar in order to get this code.</p>' +
                '<p>If you do not yet have the authorization code, you can finalize this ' +
                'transaction, however you will need to go back and enter the code later for the ' +
                'transfer to go through.</p>',
              { withHTML: true },
            )}
          />
        </Base>
        <AuthInfoForm
          translator={this.props.translator}
          initialValues={{
            authinfo: product.payload.authinfo || '',
          }}
          onSubmit={this.saveAuthInfo}
        />
        <Base marginBottom="base" flex flexJustifyBetween flexWrap>
          {formButtons}
        </Base>
      </>
    );
  }

  /**
   * Render the component
   */
  render(): React.Node {
    return <Layout>{this.renderContent()}</Layout>;
  }
}

const mapStateToProps = (
  state: FullState,
  ownProps: WithRouterProps<Params>,
) => ({
  product: selectOneProductById(state, {
    productId: ownProps.params.productId,
  }),
});

export default (compose(
  needsAuth,
  withRouter,
  withTranslator({ propsNamespace: 'translator' }),
  withLogger('AuthInfoConfiguration'),
  withPageMeta(args => ({
    title: args.__('Enter your authinfo code - Gandi.net'),
  })),
  connect(mapStateToProps),
)(AuthInfo): React.ComponentType<{}>);
